/** Review page · "Why this resume match score" — read-only. Everything shown is what the resume
 *  scorer stored with the number (api utils/resumeMatchView.ts): each rubric component's score and
 *  weight, the verbatim resume text it stands on, what the requirement asks for that the resume
 *  does not show, and the requirement profile the resume was compared with. */
import type { ReactNode } from "react";

export interface ResumeMatchView {
  kind: "rubric" | "legacy";
  components: { id: string; label: string; weight: number | null; score: number | null; evidence: string; gaps: string[] }[];
  missing: string[];
  flags: string[];
  requirement: { role: string; minYears: number | null; mustHaves: string[]; niceToHaves: string[]; domain: string } | null;
  evidencedYears: number | null;
  summary: string | null;
}

/** Same bar colours as the rest of the review page (.t1-bt: green, mid = orange, low = red). */
const tone = (s: number | null): string => (s == null || s >= 70 ? "" : s >= 45 ? "mid" : "low");

export function ResumeMatchExplain({ score, match }: { score: number | null; match: ResumeMatchView | null | undefined }): ReactNode {
  if (!match) {
    return (
      <p className="mut sm" style={{ margin: 0 }}>
        No breakdown is stored for this score — it was scored before explanations were kept. Re-scoring the resume
        records one.
      </p>
    );
  }
  const strong = match.components.filter((c) => (c.score ?? 0) >= 60);
  const weak = match.components.filter((c) => c.score != null && c.score < 60);
  const req = match.requirement;
  return (
    <div className="t1-rme">
      <p className="mut sm" style={{ marginTop: 0 }}>
        {score != null ? <b>{Math.round(score)}%</b> : "This score"} comes from comparing the resume with this
        requirement's job description
        {match.kind === "rubric" ? " on five weighted areas — the overall is their weighted average" : ""}. Read-only; a
        human decides.
      </p>

      <h4>Score breakdown</h4>
      <div className="t1-rme-rows">
        {match.components.map((c) => (
          <div key={c.id} className="t1-rme-row">
            <div className="t1-rme-row-h">
              <span>
                {c.label}
                {c.weight != null && <span className="mut"> · weight {c.weight}%</span>}
              </span>
              <b className="tnum">{c.score != null ? `${c.score}%` : "—"}</b>
            </div>
            <div className="t1-bt">
              <i className={tone(c.score)} style={{ width: `${c.score ?? 0}%` }} />
            </div>
          </div>
        ))}
      </div>

      {strong.length > 0 && (
        <>
          <h4>Matched</h4>
          <ul className="t1-rme-list">
            {strong.map((c) => (
              <li key={c.id}>
                <b>{c.label}</b>
                {c.evidence ? <blockquote>“{c.evidence}”</blockquote> : null}
              </li>
            ))}
          </ul>
        </>
      )}

      {(match.missing.length > 0 || weak.length > 0) && (
        <>
          <h4>Missing or weak</h4>
          <ul className="t1-rme-list">
            {weak.map((c) => (
              <li key={c.id}>
                <b>{c.label}</b> <span className="mut">· {c.score}%</span>
                {c.evidence ? <blockquote>“{c.evidence}”</blockquote> : <div className="mut sm">No resume text supports this area.</div>}
                {c.gaps.length > 0 && <div className="sm">Not found: {c.gaps.join("; ")}</div>}
              </li>
            ))}
            {weak.length === 0 && match.missing.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </>
      )}

      {match.flags.length > 0 && (
        <>
          <h4>Facts noted</h4>
          <ul className="t1-rme-list">
            {match.flags.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </>
      )}

      {req && (
        <>
          <h4>Compared against (from the job description)</h4>
          <dl className="t1-rme-req">
            {req.role && (
              <>
                <dt>Role</dt>
                <dd>{req.role}</dd>
              </>
            )}
            <dt>Experience</dt>
            <dd>
              {req.minYears != null ? `${req.minYears}+ years asked` : "No minimum stated"}
              {match.evidencedYears != null && ` · ${match.evidencedYears} relevant years on the resume`}
            </dd>
            {req.mustHaves.length > 0 && (
              <>
                <dt>Must-haves</dt>
                <dd>{req.mustHaves.join(", ")}</dd>
              </>
            )}
            {req.niceToHaves.length > 0 && (
              <>
                <dt>Nice-to-haves</dt>
                <dd>{req.niceToHaves.join(", ")}</dd>
              </>
            )}
            {req.domain && (
              <>
                <dt>Domain</dt>
                <dd>{req.domain}</dd>
              </>
            )}
          </dl>
        </>
      )}
      {match.summary && match.kind === "legacy" && <p className="mut sm">{match.summary}</p>}
    </div>
  );
}
